import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState, } from "react";
import { applyPalette, readStoredPalette, writeStoredPalette, } from "../lib/palette";
import { ThemeContext } from "../lib/theme-context";
import { applyThemeMode, readStoredThemeMode, resolveTheme, subscribeToSystemTheme, writeStoredThemeMode, } from "../lib/theme-mode";
/**
 * Owns the appearance for the whole app.
 *
 * Wrap the tree once, at the root. Components never read this: they say
 * `bg-surface` and the token underneath changes (docs/010) — a component that
 * branches on `resolvedTheme` to pick a colour is the bug this design prevents.
 */
export function ThemeProvider({ children, initialMode, initialPalette, }) {
    const [mode, setModeState] = useState(() => initialMode ?? readStoredThemeMode());
    const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(mode));
    // Apply on mount and on every change. Idempotent, so StrictMode's
    // double-invoke is harmless. The system subscription exists only while the
    // mode is `system` — a pinned mode must not move when the OS does.
    useEffect(() => {
        setResolvedTheme(applyThemeMode(mode));
        if (mode !== "system")
            return undefined;
        return subscribeToSystemTheme(() => {
            setResolvedTheme(applyThemeMode("system"));
        });
    }, [mode]);
    const [palette, setPaletteState] = useState(() => initialPalette ?? readStoredPalette());
    // The palette is a plain attribute write — no OS signal to follow, so no subscription.
    useEffect(() => {
        applyPalette(palette);
    }, [palette]);
    const setPalette = useCallback((next) => {
        // Persist before applying, for the same reload-right-after-the-click reason as setMode.
        writeStoredPalette(next);
        setPaletteState(next);
    }, []);
    const setMode = useCallback((next) => {
        // Persist first: a reload immediately after the click must land on the new
        // choice, and the pre-paint script only ever reads storage. The effect
        // above does the applying.
        writeStoredThemeMode(next);
        setModeState(next);
    }, []);
    const value = useMemo(() => ({ mode, resolvedTheme, setMode, palette, setPalette }), [mode, resolvedTheme, setMode, palette, setPalette]);
    return _jsx(ThemeContext, { value: value, children: children });
}
ThemeProvider.displayName = "ThemeProvider";
//# sourceMappingURL=theme-provider.js.map